import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";
// Imports of the module

import { ExternalLink } from "@/components/ExternalLink";
import i18n from "@/i18n";
import { formatNumber, parseDateWithHoursUTC } from "@/lib/utils";
import { Link } from "@heroui/link";
import { ColumnDef } from "@tanstack/react-table";
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  useDisclosure,
} from "@heroui/react";
import { useState } from "react";
import { statusOptions } from "./filter";
import { statusOptions as pcStatusOptions } from "../ProviderConfig/filter";
import { CheckIcon } from "@radix-ui/react-icons";

// Create a mapping from numeric status to status label
const getStatusLabel = (
  statusValue: number | null | undefined,
): string | null => {
  if (statusValue === null || statusValue === undefined) return null;

  const statusIndex = Number(statusValue);
  return statusOptions[statusIndex]?.label || String(statusValue);
};

const PC_STATUS_COLORS: Record<number, string> = {
  0: "bg-green-100 text-green-700",
  1: "bg-red-100 text-red-700",
  2: "bg-yellow-100 text-yellow-700",
  3: "bg-blue-100 text-blue-700",
  4: "bg-orange-100 text-orange-700",
};

const getPcStatusLabel = (value: number | null | undefined): string | null => {
  if (value === null || value === undefined) return null;
  const option = pcStatusOptions.find((o) => o.value === String(value));
  return option?.label ?? String(value);
};

const PcStatusChip = ({ status }: { status: number | null | undefined }) => {
  const label = getPcStatusLabel(status);
  if (label === null) return null;
  const colorClass =
    PC_STATUS_COLORS[Number(status)] ?? "bg-gray-100 text-gray-600";
  return (
    <span
      title="Provider Config status"
      className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-medium leading-tight ${colorClass}`}
    >
      {label}
    </span>
  );
};

const FileNameCell = ({ fileName }: { fileName: string }) => {
  const { isOpen, onOpen, onOpenChange, onClose } = useDisclosure();
  const [isCopied, setIsCopied] = useState(false);

  const maxLength = 70;
  const displayVal =
    fileName.length > maxLength
      ? `${fileName.slice(0, maxLength)}...`
      : fileName;

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(fileName);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 1500);
    } catch {
      // ignore copy failures (browser permissions, unavailable clipboard, etc.)
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={onOpen}
        className="text-xs text-left hover:underline text-primary"
      >
        {displayVal}
      </button>

      <Modal backdrop="blur" isOpen={isOpen} onOpenChange={onOpenChange}>
        <ModalContent>
          <ModalHeader>File Name</ModalHeader>
          <ModalBody>
            <div className="text-sm break-all">{fileName}</div>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={onClose}>
              Close
            </Button>
            <Button color="primary" onPress={onCopy}>
              {isCopied ? "Copied" : "Copy"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

// Type of the fields in the table

export type TimeSerieFile = {
  id: number;
  source_folder?: string | null;
  provider_config_id?: number | null;
  provider_config_code?: string | null;
  provider_config_status?: number | null;
  file_name?: string | null; // file_file_name
  metadata?: Array<{ name: string; value: string }> | null;
  is_manually_uploaded?: boolean;
  nb_strategy_ids?: number;
  strategy_ids?: number[];
  status?: number | string;
  error_messages?: any[]; // could be array or object, use array for count
  warning_messages?: any[];
  unknown_instruments?: any[];
  created_at?: string;
  loaded_at?: string | null; // loaded_updated_at
};

// Definition of the columns

export const time_serie_files_columns: ColumnDef<TimeSerieFile>[] = [
  {
    accessorKey: "id",
    header: "Id",
  },
  {
    accessorKey: "provider_config_code",
    header: "Provider Configuration",
    cell: ({ row }) => {
      const providerConfigId = row.original.provider_config_id;
      const providerConfigCode = row.original.provider_config_code;
      const providerConfigStatus = row.original.provider_config_status;

      if (providerConfigId && providerConfigCode) {
        return (
          <div className="flex flex-col items-start gap-0.5">
            <ExternalLink
              href={toWorkspacePath(
                `/admin/qtl - etl/provider_configs/${providerConfigId}`,
              )}
              className="text-xs"
              underline="hover"
            >
              {providerConfigCode}
            </ExternalLink>
            <PcStatusChip status={providerConfigStatus} />
          </div>
        );
      }
      return null;
    },
  },
  {
    accessorKey: "file_file_name",
    header: "File Name",
    cell: ({ row }) => {
      const val = row.getValue("file_file_name");
      if (!val) return null;

      const strVal = String(val);
      return <FileNameCell fileName={strVal} />;
    },
  },
  {
    accessorKey: "is_manually_uploaded",
    header: "Manually Uploaded",
    cell: ({ row }) => {
      const isManual = row.getValue("is_manually_uploaded");
      return (
        isManual && (
          <div className="pl-5">
            <CheckIcon />
          </div>
        )
      );
    },
  },
  {
    accessorKey: "nb_strategy_ids",
    header: "Number of Strategies",
    cell: ({ row }) => {
      let nb = row.getValue("nb_strategy_ids");
      if (nb == null && Array.isArray(row.original.strategy_ids)) {
        nb = row.original.strategy_ids.length;
      }
      return (
        <div className="text-right">
          {typeof nb === "number" ? formatNumber(nb) : 0}
        </div>
      );
    },
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => {
      const statusValue = row.getValue("status") as number | null | undefined;
      const statusLabel = getStatusLabel(statusValue);

      if (statusLabel === null) return null;
      return <span className="text-xs">{statusLabel}</span>;
    },
  },
  {
    accessorKey: "created_at",
    header: "Created At",
    cell: ({ row }) => {
      const val = row.getValue("created_at");
      return val ? parseDateWithHoursUTC(val as string) : null;
    },
  },
  {
    accessorKey: "loaded_at",
    header: "Loaded At",
    cell: ({ row }) => {
      // loaded_updated_at in SQL, may be mapped in data to loaded_at
      const val =
        row.getValue("loaded_at") || (row.original as any).loaded_updated_at;
      return val ? parseDateWithHoursUTC(val as string) : null;
    },
  },
  {
    id: "actions",
    header: () => <div className="text-right">Actions</div>,
    cell: ({ row }) => {
      return (
        <div className="flex justify-end">
          <Link
            href={`${window.location.pathname}/${row.getValue("id")}`}
            className="text-xs"
            underline="hover"
          >
            {i18n.t("actions.edit")}
          </Link>
        </div>
      );
    },
  },
];
